import { Button, Space, Table, Tag, Image } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import { useAtom } from 'jotai';
import { productCategoriesAtom } from '@/atoms/productCategories';
import ProductImg from './ProductImg';
import { OrderTypeEnum, OrderTypeMap } from '@/types/order';

// 订单状态枚举
export enum OrderStatus {
  Created = 1,
  ToShip = 2,
  ToReceive = 3,
  Completed = 4,
  Canceled = 5,
  Refunded = 6,
  Deleted = 7,
}

interface OrderTableProps {
  loading: boolean;
  data?: any[];
  page: number;
  pageSize: number;
  total?: number;
  onPageChange: (page: number) => void;
  onDetail: (orderId: string, orderItem?: any) => void;
  onRefund?: (orderId: string) => void;
}

const OrderTable = ({
  loading,
  data,
  page,
  pageSize,
  total,
  onPageChange,
  onDetail,
  onRefund,
}: OrderTableProps) => {
  const [categories] = useAtom(productCategoriesAtom);
  const renderOrderStatus = (status: number) => {
    switch (status) {
      case OrderStatus.Created:
        return <Tag color="processing">已创建</Tag>;
      case OrderStatus.ToShip:
        return <Tag color="warning">待发货</Tag>;
      case OrderStatus.ToReceive:
        return <Tag color="warning">待收货</Tag>;
      case OrderStatus.Completed:
        return <Tag color="success">已完成</Tag>;
      case OrderStatus.Canceled:
        return <Tag color="default">已取消</Tag>;
      case OrderStatus.Refunded:
        return <Tag color="error">已退款</Tag>;
      case OrderStatus.Deleted:
        return <Tag color="default">已删除</Tag>;
      default:
        return <Tag color="default">未知</Tag>;
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: '订单ID',
      dataIndex: 'orderId',
      width: 120,
    },
    {
      title: '展示图',
      dataIndex: 'productShowImages',
      width: 60,
      render: (record, index) => {
        return <ProductImg value={record} limit={1} />
      },
    },
    {
      title: '商品名称',
      dataIndex: 'productTitle',
      width: 200,
      ellipsis: true,
    },
    {
      title: '产品类型',
      dataIndex: 'categoryId',
      width: 120,
      render: (value: string) => {
        const category = categories?.find((c) => {
          return String(c.productCategoryId) === String(value);
        });
        return category?.categoryTitle || '-';
      },
    },
    {
      title: 'SUPP ID',
      dataIndex: 'userNumber',
      width: 100,
    },
    {
      title: '用户名',
      dataIndex: 'userName',
      width: 120,
      render: (value: string) => value || '-',
    },

    {
      title: '商品数量',
      dataIndex: 'productCount',
      width: 100,
    },
    {
      title: '订单类型',
      dataIndex: 'orderType',
      render: (value: number) => {
        if (!value) return '-';
        // E15: 金币类订单统一显示，不再区分"充值金币"和"活动金币"
        if (value === OrderTypeEnum.COINS_WORD || value === OrderTypeEnum.SUPS_WORD) {
          return '金币提示词订单';
        }
        if (value === OrderTypeEnum.COINS || value === OrderTypeEnum.SUPS) {
          return '金币商品订单';
        }
        return OrderTypeMap.get(value);
      }
    },
    {
      title: '金额',
      dataIndex: 'sumPoints',
      width: 180,
      render: (_: any, record: any) => {
        if (record.coinDetails && record.coinDetails.length > 0) {
          return (
            <div style={{ fontSize: 12, lineHeight: '20px' }}>
              {record.coinDetails.map((detail: any, idx: number) => (
                <div key={idx}>
                  {detail.coinSourceName}: {detail.coins}
                </div>
              ))}
            </div>
          );
        }
        return record.sumPoints ?? '-';
      },
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatus',
      width: 100,
      render: (value: number) => renderOrderStatus(value),
    },
    {
      title: '下单时间',
      dataIndex: 'createTime',
      width: 160,
      render: (value: string) => dayjs(value).format(FORMAT_TIME),
    },
    {
      title: '支付时间',
      dataIndex: 'payTime',
      width: 160,
      render: (value: string) =>
        value ? dayjs(value).format(FORMAT_TIME) : '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Button
            type="link"
            size="small"
            onClick={() => onDetail(record.orderId, record)}
          >
            查看详情
          </Button>
          {record.orderStatus !== OrderStatus.Refunded && onRefund && (
            <Button
              type="link"
              size="small"
              danger
              onClick={() => onRefund(record.orderId)}
            >
              退款
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      loading={loading}
      columns={columns}
      dataSource={data}
      rowKey="orderId"
      size="small"
      scroll={{ x: 'max-content' }}
      pagination={{
        current: page,
        pageSize,
        total,
        onChange: onPageChange,
        showSizeChanger: false,
      }}
    />
  );
};

export default OrderTable;
